import { CITY } from '@city'
import { useTranslation } from 'react-i18next'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { hasConsentWindow, showConsentWindow } from '../../cookie/inMobiChoice'
import appIconLight from './assets/app-icon-light.svg'
import linkArrow from './assets/footer-link-arrow.svg'
import styles from './SiteFooter.module.css'

const SUPPORT_MAIL = 'mailto:support@mmthstudio.com'

/**
 * Site footer — Figma 8597:44659 (TASK-699). Brand + blurb, Support
 * and Legal columns over vertical dividers, copyright row with the
 * TfL non-affiliation disclaimer. Legal links go to the English
 * pages whatever the page's locale — the documents exist in English
 * only (`SiteUrls.LEGAL_LOCALES`); the design's "Accessibility Statement"
 * entry is omitted until that page exists (a dead link would 302 to
 * the map — TASK-592 lesson). Support entries go to the studio
 * mailbox. "Cookie settings" re-opens the consent window (PECR: a choice
 * can be changed) and shows only on a page that runs it (TASK-1162).
 */
export function SiteFooter(): ReactElement {
  const { t } = useTranslation('station-page')
  const legalLinks = [
    { label: t('footer_privacy'), to: '/en/privacy-policy' },
    { label: t('footer_terms'), to: '/en/terms-and-conditions' },
    { label: t('footer_cookie'), to: '/en/cookie-policy' },
  ]

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.columns}>
          <div className={styles.brandColumn}>
            <div className={styles.logoRow}>
              <img src={appIconLight} alt="" width={56} height={56} />
              <span className={styles.wordmark}>{CITY.wordmark}</span>
            </div>
            <p className={styles.blurb}>{t('footer_blurb')}</p>
          </div>
          <div className={styles.linkColumn}>
            <p className={styles.columnHead}>{t('footer_support')}</p>
            <ul className={styles.linkList}>
              <li>
                <a className={styles.link} href={SUPPORT_MAIL}>
                  {t('footer_contact_us')}
                  <img src={linkArrow} alt="" width={13} height={13} />
                </a>
              </li>
              <li>
                <a
                  className={styles.link}
                  href={`${SUPPORT_MAIL}?subject=${encodeURIComponent('Help & feedback')}`}
                >
                  {t('footer_help_feedback')}
                  <img src={linkArrow} alt="" width={13} height={13} />
                </a>
              </li>
            </ul>
          </div>
          <div className={styles.linkColumn}>
            <p className={styles.columnHead}>{t('footer_legal')}</p>
            <ul className={styles.linkList}>
              {legalLinks.map(({ label, to }) => (
                <li key={label}>
                  <Link className={styles.link} to={to}>
                    {label}
                    <img src={linkArrow} alt="" width={13} height={13} />
                  </Link>
                </li>
              ))}
              {hasConsentWindow() && (
                <li>
                  <button
                    type="button"
                    className={`${styles.link} ${styles.linkButton}`}
                    onClick={showConsentWindow}
                  >
                    {t('footer_cookie_settings')}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className={styles.bottomRow}>
          <span className={styles.copyright}>
            {t('footer_copyright', { year: new Date().getFullYear() })}
          </span>
          <span className={styles.disclaimer}>{t('footer_disclaimer')}</span>
        </div>
      </div>
    </footer>
  )
}
